import type {
  MemoryKindValue,
  StaffMemoryCommandOutcome,
  StaffMemoryRecordBody,
} from "@lafayette/schemas";
import { useState } from "react";

import { SelectField, TextField } from "../../ui/fields";
import type { MemoryCommand, MemoryCommandResult } from "../commands";
import { kindOptions, sourceReferenceKindLabels } from "../labels";
import {
  emptyValueDraft,
  isUuid,
  validateValueDraft,
  type ValueDraft,
  valueFromDraft,
} from "../value-form";
import { MemoryDialog, type Reason, ReasonSelect, useCommandForm } from "./MemoryDialog";
import { ValueField } from "./ValueField";

type Props = Readonly<{
  customerId: string;
  run: (request: MemoryCommand) => Promise<MemoryCommandResult>;
  onDone: (outcome: StaffMemoryCommandOutcome) => void;
  onClose: () => void;
}>;

type Source = StaffMemoryRecordBody["sourceType"];
type ReferenceKind = "TRIP" | "CONCIERGE_REQUEST";
type Errors = Partial<Record<"value" | "reason" | "reference", string>>;

const sourceOptions = [
  { value: "STAFF_ENTRY", label: "I am entering it for the customer" },
  { value: "HISTORICAL_TRIP_OUTCOME", label: "It comes from a trip or a request" },
] as const;
const statusOptions = [
  { value: "ACTIVE", label: "Active now (the customer told me)" },
  { value: "CANDIDATE", label: "Candidate (to be reviewed later)" },
] as const;
const referenceOptions = (["TRIP", "CONCIERGE_REQUEST"] as const).map((value) => ({
  value,
  label: sourceReferenceKindLabels[value],
}));

/** M-S4. A staff entry: one fact, a closed reason, and a status of ACTIVE or CANDIDATE. */
export function AddFactDialog({ customerId, run, onDone, onClose }: Props) {
  const [kind, setKind] = useState<MemoryKindValue>("DIETARY_PREFERENCE");
  const [draft, setDraft] = useState<ValueDraft>(emptyValueDraft);
  const [source, setSource] = useState<Source>("STAFF_ENTRY");
  const [referenceKind, setReferenceKind] = useState<ReferenceKind>("TRIP");
  const [referenceId, setReferenceId] = useState("");
  const [status, setStatus] = useState<StaffMemoryRecordBody["requestedStatus"]>("ACTIVE");
  const [reason, setReason] = useState<Reason>("");
  const [errors, setErrors] = useState<Errors>({});
  const form = useCommandForm(onDone);

  function validate() {
    const next: Errors = {};
    const valueError = validateValueDraft(kind, draft);
    if (valueError !== undefined) next.value = valueError;
    if (reason === "") next.reason = "Choose a reason.";
    if (source === "HISTORICAL_TRIP_OUTCOME" && !isUuid(referenceId)) {
      next.reference = "Enter the id of the trip or request. It is a long code with dashes.";
    }
    setErrors(next);
    return Object.keys(next).length === 0 && reason !== "";
  }

  function submit() {
    if (reason === "") return;
    void form.submit(() =>
      run({
        command: "record",
        customerId,
        body: {
          kind,
          value: valueFromDraft(kind, draft),
          sourceType: source,
          sourceReference:
            source === "STAFF_ENTRY"
              ? null
              : { kind: referenceKind, id: referenceId.trim().toLowerCase() },
          requestedStatus: status,
          reasonCode: reason,
        },
      }),
    );
  }

  // A 422 names one field. The message goes under that field; any other refusal shows as a notice.
  const invalid = form.failure?.invalidField ?? null;
  const fieldMessage = form.failure?.message;
  const underField =
    invalid === "value" || invalid === "reasonCode" || invalid === "sourceReference";

  return (
    <MemoryDialog
      title="Add a memory"
      description="Add something the customer told you. It is saved with your reason."
      confirmLabel="Add memory"
      failure={underField ? null : form.failure}
      pending={form.pending}
      validate={validate}
      onSubmit={submit}
      onClose={onClose}
    >
      <SelectField
        id="memory-add-kind"
        label="Kind"
        value={kind}
        onChange={(next) => {
          setKind(next as MemoryKindValue);
          setDraft(emptyValueDraft);
        }}
        options={kindOptions}
        required
      />
      <ValueField
        idPrefix="memory-add"
        kind={kind}
        draft={draft}
        onChange={setDraft}
        error={invalid === "value" ? fieldMessage : errors.value}
      />
      <SelectField
        id="memory-add-source"
        label="Where does it come from?"
        value={source}
        onChange={(next) => setSource(next as Source)}
        options={sourceOptions}
        required
      />
      {source === "HISTORICAL_TRIP_OUTCOME" ? (
        <>
          <SelectField
            id="memory-add-reference-kind"
            label="It comes from"
            value={referenceKind}
            onChange={(next) => setReferenceKind(next as ReferenceKind)}
            options={referenceOptions}
            required
          />
          <TextField
            id="memory-add-reference-id"
            label="Trip or request id"
            value={referenceId}
            onChange={setReferenceId}
            required
            error={invalid === "sourceReference" ? fieldMessage : errors.reference}
          />
        </>
      ) : null}
      <SelectField
        id="memory-add-status"
        label="Status"
        value={status}
        onChange={(next) => setStatus(next as StaffMemoryRecordBody["requestedStatus"])}
        options={statusOptions}
        required
      />
      <ReasonSelect
        value={reason}
        onChange={setReason}
        error={invalid === "reasonCode" ? fieldMessage : errors.reason}
      />
    </MemoryDialog>
  );
}
